import {
  Button,
  CheckIcon,
  ConfirmModal,
  EllipsisVerticalIcon,
  GetPageFromApi,
  Heading,
  KeyIcon,
  PenIcon,
  Popover,
  SearchInput,
  ServerPaginatedGrid,
  WarningIcon,
} from "@wexinc/wex-ui";
import classNames from "classnames";
import { debounce, omit } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";
import { Cell, Column } from "react-table";
import { useCopSecurityService } from "services/security";
import {
  getPageOfUsers,
  promoteToSuperAdmin,
  reactivateUser,
  removeSuperAdmin,
  terminateUserSessions,
  updateGlobalUserRoles,
} from "services/users/api";
import { GlobalUserRoles, UserFromAPI } from "services/users/models";
import EditGlobalRolesModal from "./components/EditGlobalRolesModal";

function getRoleColorClassname(roleId: string) {
  switch (true) {
    case roleId === GlobalUserRoles.accountManager:
      return "bg-[#d8b4fe] text-[#581c87] font-bold";
    case roleId === GlobalUserRoles.support:
      return "bg-primary-20 text-primary-60 font-bold";
    default:
      return "bg-neutral-dark-10 text-neutral-dark-60 font-bold";
  }
}

interface ActionsMenuProps {
  user: UserFromAPI;
  onEditRoles: (user: UserFromAPI) => void;
  onToggleSuperAdmin: (user: UserFromAPI) => void;
  onTerminateSessions: (user: UserFromAPI) => void;
  onReactivate: (user: UserFromAPI) => void;
  isSuperAdmin: boolean;
}

export const ActionsMenu = (props: ActionsMenuProps) => {
  const { user, onEditRoles, onToggleSuperAdmin, onTerminateSessions, onReactivate, isSuperAdmin } = props;

  return (
    <Popover
      testId="show-widget-actions-menu"
      className="w-34 p-2 "
      anchorButtonClassName="w-8 h-8 flex justify-self-end items-center"
      getAnchorContent={() => (
        <div className="ml-auto">
          <EllipsisVerticalIcon />
        </div>
      )}
    >
      {() => (
        <ul>
          <li className="p-1">
            <Button
              testId="edit-widget"
              buttonType="secondary-neutral-dark"
              className="w-full"
              align="left"
              onClick={() => onEditRoles(user)}
            >
              <PenIcon className="mr-2" />
              Edit Roles
            </Button>
          </li>
          {isSuperAdmin && (
            <li className="p-1">
              <Button
                testId="sign-out-everywhere"
                buttonType="secondary-neutral-dark"
                className="w-full"
                align="left"
                onClick={() => onTerminateSessions(user)}
              >
                <WarningIcon className="mr-2 h-3.5 w-3.5" />
                Sign Out Everywhere
              </Button>
            </li>
          )}
          {isSuperAdmin && (
            <li className="p-1">
              <Button
                testId="toggle-sa"
                buttonType="secondary-neutral-dark"
                className="w-full"
                align="left"
                onClick={() => onToggleSuperAdmin(user)}
              >
                <KeyIcon className="mr-2 h-3.5 w-3.5" />
                {user.isSuperAdmin ? "Revoke" : "Promote"}
              </Button>
            </li>
          )}
          {user.active === false && (
            <li className="p-1">
              <Button
                testId="reactivate-user"
                buttonType="secondary-neutral-dark"
                className="w-full"
                align="left"
                onClick={() => onReactivate(user)}
              >
                Reactivate
              </Button>
            </li>
          )}
        </ul>
      )}
    </Popover>
  );
};

const Users: React.FC = () => {
  //edit roles
  const [showRolesModal, setShowRolesModal] = useState<boolean>(false);
  const [showSuperadminModal, setShowSuperadminModal] = useState<boolean>(false);
  const [showTerminateModal, setShowTerminateModal] = useState<boolean>(false);
  const [terminated, setTerminated] = useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] = useState<boolean>(false);
  const [userToEdit, setUserToEdit] = useState<UserFromAPI>();

  const { showBoundary } = useErrorBoundary();
  const { securityStore, logout } = useCopSecurityService();

  const otherParams = useRef({});

  const onSearch = debounce((value?: string) => {
    if (value) {
      setSearchParams((p) => {
        p.set("query", value);
        return p;
      });
      otherParams.current = { ...otherParams.current, query: value };
    } else {
      setSearchParams((p) => {
        p.delete("query");
        return p;
      });
      otherParams.current = omit(otherParams.current, "query");
    }
  }, 300);
  const [searchParams, setSearchParams] = useSearchParams();

  const getUsers = async (page?: number, size?: number) => {
    const query = searchParams.get("query") || undefined;
    const includeInactive = searchParams.get("includeInactive") === "true";
    const result = await getPageOfUsers(page, size, query, includeInactive);
    return result;
  };

  const onToggleIncludeInactive = (checked: boolean) => {
    setSearchParams((p) => {
      if (checked) {
        p.set("includeInactive", "true");
      } else {
        p.delete("includeInactive");
      }
      return p;
    });
    otherParams.current = checked
      ? { ...otherParams.current, includeInactive: true }
      : omit(otherParams.current, "includeInactive");
  };

  async function onSaveRoles(userId: string, roleIds: string[]) {
    try {
      await updateGlobalUserRoles(userId, roleIds);
      closeEditModal();
      otherParams.current = { ...otherParams.current };
    } catch (error) {
      showBoundary(error);
    }
  }

  async function onToggleSuperAdmin(user: UserFromAPI) {
    try {
      if (user.isSuperAdmin) {
        await removeSuperAdmin(user.email);
      } else {
        await promoteToSuperAdmin(user.email);
      }
    } catch (error) {
      showBoundary(error);
    } finally {
      closeSuperadminModal();
      otherParams.current = { ...otherParams.current };
    }
  }

  const openEditModal = useCallback((user: UserFromAPI) => {
    setUserToEdit(user);
    setShowRolesModal(true);
  }, []);

  const closeEditModal = () => {
    setUserToEdit(undefined);
    setShowRolesModal(false);
  };

  const openSuperadminModal = useCallback((user: UserFromAPI) => {
    setUserToEdit(user);
    setShowSuperadminModal(true);
  }, []);
  const closeSuperadminModal = () => {
    setUserToEdit(undefined);
    setShowSuperadminModal(false);
  };

  const openTerminateModal = useCallback((user: UserFromAPI) => {
    setUserToEdit(user);
    setShowTerminateModal(true);
  }, []);
  const closeTerminateModal = () => {
    setUserToEdit(undefined);
    setShowTerminateModal(false);
    setTerminated(false);
  };

  async function onTerminateSessions(user: UserFromAPI) {
    try {
      await terminateUserSessions(user.id);
      if (user.id === securityStore.loggedInUser?.id) {
        // terminating your own sessions signs you out everywhere - log out cleanly and redirect to login
        await logout();
        return;
      }
      setTerminated(true);
    } catch (error) {
      showBoundary(error);
    }
  }

  async function onReactivate(user: UserFromAPI) {
    try {
      await reactivateUser(user.id);
    } catch (error) {
      showBoundary(error);
    } finally {
      closeReactivateModal();
      otherParams.current = { ...otherParams.current };
    }
  }

  const openReactivateModal = useCallback((user: UserFromAPI) => {
    setUserToEdit(user);
    setShowReactivateModal(true);
  }, []);
  const closeReactivateModal = () => {
    setUserToEdit(undefined);
    setShowReactivateModal(false);
  };

  return (
    <>
      <div className="items-center justify-between">
        <div className="ml-8 py-3">
          <Heading
            testId="user-page"
            size="large"
          >
            Users
          </Heading>
        </div>
        <div className="flex items-center justify-between px-8 border-b border-t border-t-neutral-dark-20 border-b-neutral-dark-20">
          <SearchInput
            containerClassName="border-none h-9 w-48"
            placeholder={searchParams.get("query") || "Search" /* no way to set default value? */}
            onChange={(e) => onSearch(e.currentTarget.value)}
            name="search-organizations"
          />
          <label
            className="flex items-center gap-2 ml-4 text-sm text-neutral-dark-60 cursor-pointer whitespace-nowrap"
            data-testid="include-inactive-toggle"
          >
            <input
              type="checkbox"
              checked={searchParams.get("includeInactive") === "true"}
              onChange={(e) => onToggleIncludeInactive(e.currentTarget.checked)}
            />
            Show inactive users too?
          </label>
        </div>
        <div className="px-6 my-4">
          <UsersGrid
            getPageOfUsers={getUsers}
            otherParams={otherParams.current}
            openEditModal={openEditModal}
            openSuperadminModal={openSuperadminModal}
            openTerminateModal={openTerminateModal}
            openReactivateModal={openReactivateModal}
          />
        </div>
      </div>
      {showRolesModal && (
        <EditGlobalRolesModal
          user={userToEdit!}
          onCancel={closeEditModal}
          onSaveRoles={onSaveRoles}
        />
      )}
      {showSuperadminModal && (
        <ConfirmModal
          onOk={() => onToggleSuperAdmin(userToEdit!)}
          onCancel={closeSuperadminModal}
          title={
            userToEdit!.isSuperAdmin
              ? `Revoke Super Admin: ${userToEdit?.firstName} ${userToEdit?.lastName}`
              : `Promote To Super Admin: ${userToEdit?.firstName} ${userToEdit?.lastName}`
          }
        >
          <p>
            Super Admins have access to all resources, including the most sensitive data. Please use caution when giving
            or taking away these permissions.
          </p>
        </ConfirmModal>
      )}
      {showTerminateModal && (
        <ConfirmModal
          mode="critical"
          showCancelButton={!terminated}
          okButtonText={terminated ? "OK" : "Sign out everywhere"}
          onOk={() => (terminated ? closeTerminateModal() : onTerminateSessions(userToEdit!))}
          onCancel={closeTerminateModal}
          title={`Sign Out Everywhere: ${userToEdit?.firstName} ${userToEdit?.lastName}`}
        >
          {terminated ? (
            <p className="flex items-center">
              <CheckIcon className="mr-2 text-utility-success-50 fill-current" />
              All sessions have been terminated. This user is signed out of COP and Okta and must log in again.
            </p>
          ) : userToEdit?.id === securityStore.loggedInUser?.id ? (
            <p>
              This will sign <strong>you</strong> out of COP everywhere and revoke your Okta sessions and tokens. You
              will be returned to the login screen and must log in again.
            </p>
          ) : (
            <p>
              This immediately signs the user out of COP everywhere and revokes their Okta sessions and tokens. They
              will need to log in again. This cannot be undone.
            </p>
          )}
        </ConfirmModal>
      )}
      {showReactivateModal && (
        <ConfirmModal
          onOk={() => onReactivate(userToEdit!)}
          onCancel={closeReactivateModal}
          title={`Reactivate User: ${userToEdit?.firstName} ${userToEdit?.lastName}`}
        >
          <p>
            Reactivating restores this user&apos;s access so they can log in again and reappear in the active users
            list.
          </p>
        </ConfirmModal>
      )}
    </>
  );
};

const getColumns = (
  onEditRoles: (user: UserFromAPI) => void,
  openSuperadminModal: (user: UserFromAPI) => void,
  openTerminateModal: (user: UserFromAPI) => void,
  openReactivateModal: (user: UserFromAPI) => void,
  isSuperAdmin: boolean,
) => {
  return [
    {
      Header: "FIRST NAME",
      accessor: "firstName",
      disableSortBy: true,
      Cell: ({ row }: Cell<UserFromAPI>) => {
        return <div className="line-clamp-1">{row.original.firstName || "--"}</div>;
      },
    },
    {
      Header: "LAST NAME",
      accessor: "lastName",
      disableSortBy: true,
      Cell: ({ row }: Cell<UserFromAPI>) => {
        return <div className="line-clamp-1">{row.original.lastName || "--"}</div>;
      },
    },
    {
      Header: "EMAIL",
      accessor: "email",
      disableSortBy: true,
      Cell: ({ row }: Cell<UserFromAPI>) => {
        return <div className="flex flex-row line-clamp-1">{row.original.email || "--"}</div>;
      },
      width: "200px",
    },
    {
      Header: "GLOBAL ROLES",
      accessor: "globalRoles",
      disableSortBy: true,
      width: "200px",
      Cell: ({ row }: Cell<UserFromAPI>) => {
        return (
          <div className="flex flex-row">
            {row.original.globalRoles.length
              ? row.original.globalRoles.map((role) => (
                  <span
                    className={classNames(
                      getRoleColorClassname(role.id),
                      "p-1 mr-1 rounded overflow-ellipsis shrink line-clamp-1",
                    )}
                    key={role.id}
                  >
                    {role.name}
                  </span>
                ))
              : "--"}
          </div>
        );
      },
    },
    {
      Header: "STATUS",
      disableSortBy: true,
      width: "100px",
      Cell: ({ row }: Cell<UserFromAPI>) => {
        const active = row.original.active !== false;
        return (
          <span
            className={classNames(
              "p-1 rounded font-bold",
              active ? "bg-primary-20 text-primary-60" : "bg-neutral-dark-10 text-neutral-dark-60",
            )}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      Header: "SUPER ADMIN",
      disableSortBy: true,
      show: isSuperAdmin,
      Cell: ({ row }: Cell<UserFromAPI>) => {
        return <div>{row.original.isSuperAdmin && <KeyIcon className="inline text-[#d4af37] h-6 w-6 mr-2" />}</div>;
      },
      width: "50px",
    },
    {
      Header: () => null,
      accessor: "actionsMenu",
      disableSortBy: true,
      Cell: ({ row }: Cell<UserFromAPI>) => {
        const user = row.original;
        return (
          <ActionsMenu
            user={user}
            onEditRoles={(user) => onEditRoles(user)}
            onToggleSuperAdmin={(user) => openSuperadminModal(user)}
            onTerminateSessions={(user) => openTerminateModal(user)}
            onReactivate={(user) => openReactivateModal(user)}
            isSuperAdmin={isSuperAdmin}
          />
        );
      },
      width: "30px",
    },
  ];
};

type UsersGrid = {
  getPageOfUsers: GetPageFromApi<UserFromAPI>;
  openEditModal: (user: UserFromAPI) => void;
  openSuperadminModal: (user: UserFromAPI) => void;
  openTerminateModal: (user: UserFromAPI) => void;
  openReactivateModal: (user: UserFromAPI) => void;
  otherParams?: object;
};

const UsersGrid: React.FC<UsersGrid> = (props) => {
  const { getPageOfUsers, otherParams, openEditModal, openSuperadminModal, openTerminateModal, openReactivateModal } =
    props;

  const { userHasSuperAdminPermissions } = useCopSecurityService();
  const isSuperAdmin = userHasSuperAdminPermissions();

  const columns = useMemo(
    () => getColumns(openEditModal, openSuperadminModal, openTerminateModal, openReactivateModal, isSuperAdmin),
    [openEditModal, openSuperadminModal, openTerminateModal, openReactivateModal, isSuperAdmin],
  ) as unknown as Array<Column<UserFromAPI>>;
  return (
    <ServerPaginatedGrid
      memoizedColumns={columns}
      getPageOfData={getPageOfUsers}
      otherParams={otherParams}
    />
  );
};

export default Users;
